import { useId } from 'react'
import { Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  useGetApiCompaniesCurrentEmployees,
  useGetApiCompaniesCurrentGroups,
} from '@/api/generated/company-management-user/company-management-user'
import { Checkbox } from '@/components/shadcn/checkbox'
import { type CheckListItem } from '@/components/common/CheckList'
import { SearchableCheckList } from '@/components/common/SearchableCheckList'

interface ModuleAssignmentFieldsProps {
  /** Company-wide assignment: when true the module is assigned to everyone and the group /
   *  employee selection is hidden. */
  isCompanyWide: boolean
  onCompanyWideChange: (value: boolean) => void
  /** Selected group ids — used only when not company-wide. */
  selectedGroupIds: number[]
  onGroupsChange: (ids: number[]) => void
  disabled?: boolean
  /** Direct employee assignment. Optional — omit both to hide the employee list
   *  (e.g. service modules, which have no direct employee assignment). */
  selectedEmployeeIds?: string[]
  onEmployeesChange?: (ids: string[]) => void
}

/**
 * Reusable module-assignment controls: a company-wide toggle plus — when not company-wide — a
 * group list and an optional direct-employee list, shown side by side. Both are searchable
 * checkbox lists. All labels come from the `common:assignment.*` namespace, so every caller
 * (action-module wizard, notification dialog, service-module editor) renders identical text.
 */
export function ModuleAssignmentFields({
  isCompanyWide,
  onCompanyWideChange,
  selectedGroupIds,
  onGroupsChange,
  disabled,
  selectedEmployeeIds,
  onEmployeesChange,
}: ModuleAssignmentFieldsProps) {
  const { t } = useTranslation('common')
  const checkboxId = useId()
  const showEmployees = selectedEmployeeIds != null && onEmployeesChange != null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id={checkboxId}
          checked={isCompanyWide}
          onCheckedChange={v => onCompanyWideChange(v === true)}
          disabled={disabled}
        />
        <label htmlFor={checkboxId} className="flex cursor-pointer items-center gap-1.5 text-sm">
          <Globe className="size-3.5" />
          {t('assignment.companyWide')}
        </label>
      </div>

      {!isCompanyWide && (
        <div className="flex flex-col gap-4 sm:flex-row">
          <GroupAssignmentList t={t} selectedGroupIds={selectedGroupIds} onGroupsChange={onGroupsChange} />
          {showEmployees && (
            <EmployeeAssignmentList
              t={t}
              selectedEmployeeIds={selectedEmployeeIds}
              onEmployeesChange={onEmployeesChange}
            />
          )}
        </div>
      )}
    </div>
  )
}

/** Group assignment list. Fetches the company's groups. */
function GroupAssignmentList({
  t,
  selectedGroupIds,
  onGroupsChange,
}: {
  t: TFunction<'common'>
  selectedGroupIds: number[]
  onGroupsChange: (ids: number[]) => void
}) {
  const { data } = useGetApiCompaniesCurrentGroups()
  const groups = (data?.data ?? []) as { id: number; name: string | null }[]
  const items: CheckListItem[] = groups.map(g => ({ id: g.id, label: g.name ?? t('assignment.groupFallback', { id: g.id }) }))

  return (
    <SearchableCheckList
      label={t('assignment.groups')}
      searchLabel={t('assignment.groupSearchPlaceholder')}
      items={items}
      selected={selectedGroupIds}
      onChange={ids => onGroupsChange(ids as number[])}
      emptyText={t('assignment.noGroups')}
    />
  )
}

/** Direct employee assignment list. Fetches the company's employees; split out so the query
 *  only fires when the section is used. */
function EmployeeAssignmentList({
  t,
  selectedEmployeeIds,
  onEmployeesChange,
}: {
  t: TFunction<'common'>
  selectedEmployeeIds: string[]
  onEmployeesChange: (ids: string[]) => void
}) {
  const { data } = useGetApiCompaniesCurrentEmployees()
  const employees = (data?.data ?? []) as { id: string | null; firstName: string | null; lastName: string | null }[]
  const items: CheckListItem[] = employees
    .filter(e => e.id)
    .map(e => ({ id: e.id!, label: [e.firstName, e.lastName].filter(Boolean).join(' ') || e.id! }))

  return (
    <SearchableCheckList
      label={t('assignment.employees')}
      searchLabel={t('assignment.employeeSearchPlaceholder')}
      items={items}
      selected={selectedEmployeeIds}
      onChange={ids => onEmployeesChange(ids as string[])}
      emptyText={t('assignment.noEmployees')}
      selectAllLabel={t('assignment.selectAll')}
    />
  )
}
